import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { LANGUAGES, useLanguage } from '../lib/LanguageContext';

/**
 * LanguageSwitcher
 * Props:
 *  - variant: "header" (default, light bg) | "sidebar" (dark bg, white text)
 *  - className: extra classes
 *  - compact: bool — if true, only flag is shown in the trigger
 */
export default function LanguageSwitcher({ variant = 'header', className = '', compact = false }) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, []);

  const current = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const isSidebar = variant === 'sidebar';

  const triggerClass = isSidebar
    ? `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 text-white/90 w-full text-left transition ${className}`
    : `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition bg-white border-slate-200 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700 ${className}`;

  const menuClass = isSidebar
    ? 'absolute z-50 bottom-full mb-2 left-0 right-0 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 min-w-[180px] overflow-hidden'
    : 'absolute z-50 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1 min-w-[180px] overflow-hidden';

  const itemClass = (active) => {
    if (isSidebar) {
      return `flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer w-full transition ${active ? 'bg-white/10 text-white font-medium' : 'text-white/80 hover:bg-white/5'}`;
    }
    return `flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer w-full transition ${active ? 'bg-blue-50 text-blue-900 font-medium dark:bg-blue-900/30 dark:text-blue-200' : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'}`;
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClass}
        data-testid="lang-switcher"
      >
        {isSidebar && <Globe className="w-4 h-4 shrink-0"/>}
        <span className="text-base leading-none" aria-hidden="true">{current.flag}</span>
        {!compact && <span className={isSidebar ? '' : 'uppercase tracking-wide'}>{isSidebar ? current.label : current.code}</span>}
        <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition ${open ? 'rotate-180' : ''}`}/>
      </button>
      {open && (
        <div role="listbox" className={menuClass}>
          {LANGUAGES.map((l) => {
            const active = l.code === language;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                type="button"
                onClick={() => { setLanguage(l.code); setOpen(false); }}
                className={itemClass(active)}
              >
                <span className="text-base leading-none" aria-hidden="true">{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {active && <Check className="w-4 h-4 opacity-80"/>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
